import { useCallback, useDeferredValue, useEffect, useMemo, useState, useTransition } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { BulkActionsDrawerProps } from './BulkActionsDrawer.props';
import { BulkActionRow, DefaultValues, FilteredCompany } from './BulkActionsDrawer.types';
import BulkActionsContentWrapper from './components/BulkActionsContentWrapper/BulkActionsContentWrapper';
import BulkActionsTable from './components/BulkActionsTable/BulkActionsTable';
import BulkActionsToolbar from './components/BulkActionsToolbar/BulkActionsToolbar';
import SelectFundMeasurementDate from './components/SelectFundMeasurementDate/SelectFundMeasurementDate';
import {
  ADJUSTMENT_SELECTED_FIELD,
  ADJUSTMENT_TYPE_SELECTION_FIELD,
  AS_OF_DATE_FIELD,
  BACKSOLVE_FIELD,
  BULK_REFRESH_MARKET_DATA_ACTION_LABEL,
  BULK_REFRESH_MARKET_DATA_APP_BAR_SUBTITLE,
  BULK_REFRESH_MARKET_DATA_APP_BAR_TITLE,
  columnHeaders,
  IS_FIRM_OWNED_FIELD,
  IS_READY_FOR_AUDIT_FIELD,
  MARKET_DATE_FIELD,
  MAX_PERCENT_CHANGE_FIELD,
  METRIC_FIELD,
  MIN_PERCENT_CHANGE_FIELD,
  OPM_BACKSOLVE_DATE_FIELD,
  STATUS,
  TRANSACTION_COMPS_FIELD,
} from './constants';
import useDefaultRowConfig from './hooks/useDefaultRowConfig/useDefaultRowConfig';
import useRetrieveDatesForFirm from './hooks/useRetrieveDatesForFirm/useRetrieveDatesForFirm';
import useRowConfig from './hooks/useRowConfig/useRowConfig';
import { ApiService, BulkBacksolveUpdate, BulkValuationUpdate, type FirmCompanyMeasurementDate } from '../../../../api';
import { useStore } from '../../../../common/store';
import { UseStoreValues } from '../../../../common/types/store';
import { useDebouncedValue } from '../../../../hooks';
import useRollingApiRequest from '../../../../hooks/useRollingApiRequest/useRollingApiRequest';

const BulkActionsDrawer = ({ open, onClose }: BulkActionsDrawerProps) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    market_date: '',
    is_ready_for_audit: undefined,
    is_firm_owned: undefined,
    status: undefined,
    update_transaction_comps: true,
    opm_backsolve_date: '',
    as_of_date: '',
    adjustment_type_selection: null,
    adjustment_selected: null,
    metric: null,
    min_percent_change: 1,
    max_percent_change: 100,
  });

  const isScalarUser = useMemo(
    () => !!(storeValue?.user?.is_staff || storeValue?.user?.is_superuser),
    [storeValue.user]
  );

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [isPending, startTransition] = useTransition();
  const [visibleSubColumns, setVisibleSubColumns] = useState<{ field: string; parentField: string }[]>([]);

  const [selectedFunds, setSelectedFunds] = useState<Set<number>>(new Set());
  const [fundSearchQuery, setFundSearchQuery] = useState('');

  const firmId = useMemo(() => storeValue.firmId, [storeValue]);
  const {
    selectedFundMeasurementDate,
    uniqueFundMeasurementDatesByDate,
    setSelectedFundMeasurementDate,
    combinedMeasurementDates,
  } = useRetrieveDatesForFirm(firmId as number | null, null);

  // Use React Query for caching and request management
  const { data: companyMeasurementDatesByFundRaw, isLoading, isFetching } = useQuery({
    queryKey: ['valuationFirm', firmId, selectedFundMeasurementDate?.date],
    queryFn: () => {
      if (!firmId || !selectedFundMeasurementDate) {
        return Promise.resolve([] as FirmCompanyMeasurementDate[]);
      }
      return ApiService.apiValuationFirmRead(firmId.toString(), selectedFundMeasurementDate.date);
    },
    enabled: !!firmId && !!selectedFundMeasurementDate,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Keep a stable empty array while loading to avoid memo/effect churn
  const companyMeasurementDatesByFund = useMemo(
    () => companyMeasurementDatesByFundRaw ?? [],
    [companyMeasurementDatesByFundRaw]
  );

  const filteredCompanies = useMemo<FilteredCompany[]>(() => {
    // Avoid unnecessary mapping when there's no search query
    // Use debouncedSearchQuery for filtering to avoid lag while typing
    if (!debouncedSearchQuery) {
      if (!debouncedSearchQuery.trim()) {
        return companyMeasurementDatesByFund;
      }
    }

    const lowerSearch = debouncedSearchQuery.toLowerCase().trim();
    return companyMeasurementDatesByFund.filter(company => company.company_name?.toLowerCase().includes(lowerSearch));
  }, [companyMeasurementDatesByFund, debouncedSearchQuery]);

  // Filter companies based on selected funds
  const filteredCompaniesByFund = useMemo(() => {
    if (selectedFunds.size === 0) {
      return filteredCompanies; // If no funds selected, show all companies
    }

    return filteredCompanies.filter(company =>
      // Check if any of the company's related_fund_ids are in the selectedFunds set
      company.related_fund_ids?.some(fundId => selectedFunds.has(Number(fundId)))
    );
  }, [filteredCompanies, selectedFunds]);

  const updateRow = useCallback((id: number, field: string, value: any) => {
    setRows(prevState =>
      prevState.map(row => {
        if (row.id === Number(id)) {
          const updatedRow = {
            ...row,
            [field]: value,
          };
          return updatedRow;
        }
        return row;
      })
    );
  }, []);

  const latestPossibleMarketDate = useMemo(() => moment().subtract(1, 'days'), []);

  const [rows, setRows] = useState<BulkActionRow[]>([]);
  const deferredRows = useDeferredValue(rows);

  // Memoize sorted rows to avoid sorting on every render
  const sortedRows = useMemo(
    () => rows.toSorted((a, b) => a.company_name?.localeCompare(b.company_name || '') || 0),
    [rows]
  );

  // Use the custom hook for rowConfig
  const rowConfig = useRowConfig({
    rows: sortedRows, // Pass sorted rows instead of unsorted
    visibleColumns,
    visibleSubColumns,
    latestPossibleMarketDate,
    updateRow,
    isScalarUser,
  });

  // Handle default value changes. One way sync to the rows
  const handleDefaultValueChange = useCallback((field: string, value: any) => {
    setDefaultValues(prev => ({
      ...prev,
      [field]: value,
    }));

    setRows(prevState =>
      prevState.map(row => {
        if (field === IS_READY_FOR_AUDIT_FIELD) {
          // If the field is ready for audit, we only want to set it if the status is not draft
          if (value && row.status === BulkValuationUpdate.status.DRAFT) {
            return row; // Don't change the row if the status is draft
          }
        }
        return {
          ...row,
          [field]: value,
        };
      })
    );
  }, []);

  // Use the custom hook for defaultRowConfig
  const defaultRowConfig = useDefaultRowConfig({
    defaultValues,
    visibleColumns,
    visibleSubColumns,
    latestPossibleMarketDate,
    handleDefaultValueChange,
    isScalarUser,
  });

  // this initializes rows when we change the FMD
  useEffect(() => {
    const updatedRows = filteredCompaniesByFund.map(company => {
      const companyId = company.id as number;
      const market_date = company.primary_allocation?.market_date || '';
      const status = company.primary_allocation?.status || BulkValuationUpdate.status.DRAFT;
      const update_transaction_comps = true;
      const is_firm_owned = company.primary_allocation?.is_firm_owned as boolean;
      const is_ready_for_audit = company.primary_allocation?.is_ready_for_audit as boolean;
      const as_of_date = undefined;
      const opm_backsolve_date = undefined;
      const adjustment_type_selection = BulkBacksolveUpdate.adjustment_type_selection.ENTERPRISE_VALUE; // Default to mean, can be changed later
      const adjustment_selected = BulkBacksolveUpdate.adjustment_selected.MEDIAN;
      const metric = BulkBacksolveUpdate.metric.MARKET_CAP;
      const min_percent_change = 1;
      const max_percent_change = 100;

      // we want to move a bunch of the stuff in the column onto the row
      return {
        id: companyId,
        valuation_id: company.primary_allocation?.valuation_id as number,
        company_name: company.company_name,
        // Priority: 1. Individual row market date, 2. Default market date, 3. Original market date
        market_date,
        status,
        update_transaction_comps,
        is_ready_for_audit,
        is_firm_owned,
        as_of_date,
        opm_backsolve_date,
        adjustment_type_selection,
        adjustment_selected,
        metric,
        min_percent_change,
        max_percent_change,
      };
    });

    // Skip state updates if the identity (id + valuation_id) is unchanged
    const rowsMatchIdentity =
      rows.length === updatedRows.length &&
      rows.every(
        (row, index) => row.id === updatedRows[index].id && row.valuation_id === updatedRows[index].valuation_id
      );

    if (rowsMatchIdentity) {
      return;
    }

    startTransition(() => {
      setRows(() => updatedRows);
    });
  }, [filteredCompaniesByFund, startTransition, rows]);

  const [selectionModel, setSelectionModel] = useState<number[]>([]);
  const isButtonDisabled = useMemo(() => selectionModel.length === 0 || isPending, [selectionModel, isPending]);

  const handleFundToggle = useCallback(
    (fundId: number) => {
      setSelectedFunds(prev => {
        const newSelected = new Set(prev);
        if (newSelected.has(fundId)) {
          newSelected.delete(fundId);

          // When deselecting a fund, deselect any company that isn't related to any of the remaining selected funds
          if (newSelected.size > 0) {
            // Get all companies related to the remaining selected funds
            const companiesRelatedToSelectedFunds = new Set<number>();

            // For each selected fund, find all related companies
            newSelected.forEach(selectedFundId => {
              const relatedCompanies = filteredCompanies.filter(company =>
                company.related_fund_ids?.some(relatedFundId => Number(relatedFundId) === selectedFundId)
              );

              // Add the company IDs to the set
              relatedCompanies.forEach(company => {
                companiesRelatedToSelectedFunds.add(Number(company.id));
              });
            });

            // Update the selection model to only include companies related to selected funds
            setSelectionModel(prev => prev.filter(companyId => companiesRelatedToSelectedFunds.has(companyId)));
          } else {
            // If no funds are selected, clear the selection model
            setSelectionModel([]);
          }
        } else {
          newSelected.add(fundId);
          // When selecting a fund, also select all companies related to that fund
          const companiesRelatedToFund = filteredCompanies.filter(company =>
            company.related_fund_ids?.some(relatedFundId => Number(relatedFundId) === fundId)
          );

          // Add all companies related to this fund to the selection model
          if (companiesRelatedToFund.length > 0) {
            const companyIds = companiesRelatedToFund.map(company => Number(company.id));
            setSelectionModel(prev => {
              const newSelectionModel = [...prev];
              companyIds.forEach(id => {
                if (!newSelectionModel.includes(id)) {
                  newSelectionModel.push(id);
                }
              });
              return newSelectionModel;
            });
          }
        }
        return newSelected;
      });
    },
    [filteredCompanies]
  );

  const uniqueFunds = useMemo(() => {
    if (!selectedFundMeasurementDate || combinedMeasurementDates.length === 0) {
      return [];
    }
    return combinedMeasurementDates
      .filter(fmd => fmd.date === selectedFundMeasurementDate.date)
      .map(({ fund_id, fund_name }) => {
        if (fund_id && fund_name) {
          return {
            id: fund_id,
            name: fund_name,
          };
        }
        return null;
      })
      .filter((f): f is { id: number; name: string } => f !== null);
  }, [combinedMeasurementDates, selectedFundMeasurementDate]);

  // Filter funds based on search query
  const filteredFunds = useMemo(() => {
    if (!fundSearchQuery) {
      return uniqueFunds;
    }

    return uniqueFunds.filter(fund => fund.name.toLowerCase().includes(fundSearchQuery.toLowerCase()));
  }, [uniqueFunds, fundSearchQuery]);

  // Initialize visible columns with all column fields
  useEffect(() => {
    if (visibleColumns.length === 0) {
      setVisibleColumns(columnHeaders.filter(columnHeader => columnHeader.isInitialColumn).map(column => column.field));
    }
    //   no dependencies here to ensure this runs only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubColumnToggle = (columnField: string, parentColumnField: string) => {
    setVisibleSubColumns(prev => {
      const exists = prev.some(
        subColumn => subColumn.field === columnField && subColumn.parentField === parentColumnField
      );

      if (exists) {
        return prev.filter(
          subColumn => !(subColumn.field === columnField && subColumn.parentField === parentColumnField)
        );
      }

      return [...prev, { field: columnField, parentField: parentColumnField }];
    });
  };

  const handleColumnToggle = (columnField: string) => {
    setVisibleColumns(prev => {
      const relatedColumn = columnHeaders.find(column => column.field === columnField);
      const relatedSubColumns = (relatedColumn?.subColumns || []).map(subColumn => subColumn.field);

      if (prev.includes(columnField)) {
        // Don't allow removing the last column
        if (prev.length === 1) {
          return prev;
        }
        // remove related subcolumns from visibleSubColumns as well
        if (relatedSubColumns.length > 0) {
          setVisibleSubColumns(prevSubColumns =>
            prevSubColumns.filter(subColumn => subColumn.parentField !== columnField)
          );
        }
        return prev.filter(field => field !== columnField);
      }

      if (relatedSubColumns.length > 0) {
        setVisibleSubColumns(prevSubColumns => [
          ...prevSubColumns,
          ...relatedSubColumns.map(subField => ({ field: subField, parentField: columnField })),
        ]);
      }

      return [...prev, columnField];
    });
  };

  const updateParams = useMemo(() => {
    // updated_at should be the current date and time as a string

    const currentDate = moment().format('YYYY-MM-DD HH:mm:ss');
    return deferredRows
      .filter(row => selectionModel.includes(row.id))
      .map(row => {
        //   construct an object based on the visible columns and the row valuation id
        const updateObject: BulkValuationUpdate = {
          id: row.valuation_id,
          update_requested_at: currentDate,
        };

        if (visibleColumns.includes(MARKET_DATE_FIELD)) {
          updateObject.market_date = row.market_date;
        }
        if (visibleColumns.includes(STATUS)) {
          updateObject.status = row.status;
        }
        if (visibleColumns.includes(IS_READY_FOR_AUDIT_FIELD)) {
          updateObject.is_ready_for_audit = row.is_ready_for_audit;
        }
        if (visibleColumns.includes(IS_FIRM_OWNED_FIELD)) {
          updateObject.is_firm_owned = row.is_firm_owned;
        }
        if (visibleColumns.includes(TRANSACTION_COMPS_FIELD)) {
          updateObject.update_transaction_comps = row.update_transaction_comps;
        }
        if (
          visibleColumns.includes(BACKSOLVE_FIELD) &&
          row[ADJUSTMENT_TYPE_SELECTION_FIELD] &&
          row[ADJUSTMENT_SELECTED_FIELD] &&
          row[METRIC_FIELD]
        ) {
          const backsolve: BulkBacksolveUpdate = {
            opm_backsolve_date: row[OPM_BACKSOLVE_DATE_FIELD],
            adjustment_type_selection: row[ADJUSTMENT_TYPE_SELECTION_FIELD],
            adjustment_selected: row[ADJUSTMENT_SELECTED_FIELD],
            metric: row[METRIC_FIELD],
            // todo: ensure no floating point issues
            [MIN_PERCENT_CHANGE_FIELD]: ((row[MIN_PERCENT_CHANGE_FIELD] ?? 0) / 100).toString(),
            [MAX_PERCENT_CHANGE_FIELD]: ((row[MAX_PERCENT_CHANGE_FIELD] ?? 0) / 100).toString(),
          };
          if (
            visibleSubColumns.find(
              subCol => subCol.field === AS_OF_DATE_FIELD && subCol.parentField === BACKSOLVE_FIELD
            )
          ) {
            backsolve.as_of_date = row[AS_OF_DATE_FIELD];
          }
          updateObject.backsolve = backsolve;
        }
        return updateObject;
      });
  }, [selectionModel, visibleColumns, deferredRows, visibleSubColumns]);

  const promiseHandler = useCallback(
    (payload: BulkValuationUpdate) =>
      ApiService.apiValuationIndividualUpdatePartialUpdate(payload.id.toString(), payload),
    []
  );
  const pollHandler = useCallback(
    (params: BulkValuationUpdate) =>
      ApiService.apiValuationIndividualUpdatePollRead(params.id.toString(), params.update_requested_at),
    []
  );
  const getRequestId = useCallback((params: BulkValuationUpdate) => params.id, []);

  const { result, doRequest, processStatus } = useRollingApiRequest({
    promisesParams: updateParams,
    promiseHandler,
    pollingConfig: {
      pollMethod: pollHandler,
      pollIntervalInMs: 10000,
      allowedRunTimeInMs: 60000,
    },
    getRequestId,
  });

  const onSave = useCallback(() => {
    doRequest();
  }, [doRequest]);
  const appBarProps = useMemo(
    () => ({
      onClose,
      onSave,
      isButtonDisabled,
      title: BULK_REFRESH_MARKET_DATA_APP_BAR_TITLE,
      subtitle: BULK_REFRESH_MARKET_DATA_APP_BAR_SUBTITLE,
      actionLabel: BULK_REFRESH_MARKET_DATA_ACTION_LABEL,
    }),
    [onClose, onSave, isButtonDisabled]
  );
  return (
    <BulkActionsContentWrapper isLoading={isLoading || isFetching || isPending} open={open} onClose={onClose} appBarProps={appBarProps}>
      <SelectFundMeasurementDate
        selectedFundMeasurementDateState={{
          value: selectedFundMeasurementDate,
          setter: setSelectedFundMeasurementDate,
        }}
        uniqueFundMeasurementDatesByDate={uniqueFundMeasurementDatesByDate}
        label="Measurement Date"
      />

      {selectedFundMeasurementDate && (
        <div style={{ marginTop: '2em', marginLeft: '0' }}>
          <br />
          <div style={{ height: 'calc(80vh - 200px)', width: '100%' }}>
            <BulkActionsToolbar
              searchState={{ value: searchQuery, setter: setSearchQuery }}
              selectedFunds={selectedFunds}
              fundSearchState={{ value: fundSearchQuery, setter: setFundSearchQuery }}
              filteredFunds={filteredFunds}
              handleFundToggle={handleFundToggle}
              handleColumnToggle={handleColumnToggle}
              handleSubColumnToggle={handleSubColumnToggle}
              visibleColumns={visibleColumns}
              visibleSubColumns={visibleSubColumns}
            />
            <BulkActionsTable
              selectionModelState={{
                value: selectionModel,
                setter: setSelectionModel,
              }}
              rows={rowConfig}
              visibleColumns={visibleColumns}
              visibleSubColumns={visibleSubColumns}
              saveStatus={processStatus}
              saveResults={result}
              defaultRowConfig={defaultRowConfig}
            />
          </div>
        </div>
      )}
    </BulkActionsContentWrapper>
  );
};

export default BulkActionsDrawer;
